import axios from 'axios';
import { retryWithBackoff } from '@/utils/api';
import { REMOTE_ASSETS } from '@/constants/REMOTE_ASSETS';

// ============================================================================
// STATE STRUCTURE
// ============================================================================
// Separated into two main sections:
// 1. CONTENT (Static data from CMS/API - cached, shared across all users)
// 2. PROGRESS (User-specific data - persisted per user)

// A factory rather than a constant: `state()` spreads this for every store and
// `RESET_ALL` assigns it back on logout, so one shared object literal would
// hand every store — and every reset — the SAME nested maps. Same shape #1506
// fixed in courses.js; see `initialState` there (#1524).
const createInitialState = () => ({
  // Loading states
  isLoadingContent: false, // Loading static content
  isLoadingProgress: false, // Loading user progress

  // ============================================
  // CONTENT DATA (Static - from CMS/API)
  // ============================================
  // This data is the same for all users and comes from CMS/API
  // Cached in memory, can be refreshed from API

  chapters: {
    // chapterId: { id, title, description, order, imageUrl, etc. }
    // Example: { 1: { id: 1, title: "God's Blueprint", order: 1 } }
  },

  steps: {
    // chapterId: { stepId: { id, title, description, order, content, etc. } }
    // Example: { 1: { 'step-1': { id: 'step-1', title: 'Introduction', order: 1 } } }
  },

  contentLastUpdated: null, // Timestamp of last content fetch

  // ============================================
  // PROGRESS DATA (User-specific / Member data)
  // ============================================
  // This data is unique per user/member and tracks their progress
  // Persisted to localStorage and synced with server

  memberOnboardingFlags: {
    // Key-value pairs of onboarding flags
    // Example: { hasVisitedAbsolutely: true, firstVisitAnimationPlayed: false }
  },

  memberCompletedSteps: {
    // chapterId: { stepId: boolean }
    // Example: { 1: { 'step-1': true, 'step-2': false } }
  },

  memberChapterProgress: {
    // chapterId: lastCompletedStepId
    // Example: { 1: 'step-3', 2: 'step-1' }
  },

  memberCompletedMilestones: {
    // milestoneId: true
    // Example: { 1: true, 2: true, 5: true }
    // Tracks completed milestones by their backend milestone ID
  },

  memberVideoProgress: {
    // chapterId: { stepId: videoProgressSeconds }
    // Example: { 1: { 'step-1': 120.5, 'step-2': 45.2 } }
    // Tracks video playback progress in seconds (from backend)
  },

  // ============================================
  // UI STATE (Component state)
  // ============================================
  selectedChapterId: 0, // Currently selected chapter on home page

  // ============================================
  // UNLOCK STATE
  // ============================================
  isUnlocking: false,
  unlockingError: null,

  // ============================================
  // VIDEO STATE
  // ============================================
  videoProgressAtEnd: null, // Stores video progress at completion for still frame

  // ============================================
  // INTERNAL STATE (Not persisted)
  // ============================================
  _milestoneIdMapping: {}, // Maps stepId to chapterMilestoneId: { chapterId: { stepId: milestoneId } }
});

// ============================================================================
// STORAGE HELPERS
// ============================================================================

// Load MEMBER PROGRESS from localStorage (not content - content comes from API)
// TEMPORARILY DISABLED - using API only for testing
const loadProgressFromStorage = () => {
  // Return empty objects - rely on API only
  return {
    memberOnboardingFlags: {},
    memberCompletedSteps: {},
    memberChapterProgress: {},
  };
};

// Save MEMBER PROGRESS to localStorage
// TEMPORARILY DISABLED - using API only for testing
const saveProgressToStorage = (
  memberOnboardingFlags,
  memberCompletedSteps,
  memberChapterProgress,
) => {
  // Do nothing - rely on API only
  return;
};

// Load CONTENT from localStorage cache (optional - can be refreshed from API)
// TEMPORARILY DISABLED - using API only for testing
const loadContentFromStorage = () => {
  // Return empty objects - rely on API only
  return {
    chapters: {},
    steps: {},
    contentLastUpdated: null,
  };
};

// Save CONTENT to localStorage cache
// TEMPORARILY DISABLED - using API only for testing
const saveContentToStorage = (chapters, steps) => {
  // Do nothing - rely on API only
  return;
};

// ============================================================================
// DATA TRANSFORMATION HELPERS
// ============================================================================

// Transform backend chapters response to frontend format
// Backend: [{ id, chapterNumber, chapterName, milestones: [...] }]
// Frontend: { chapterId: { id, title, order, ... }, ... }
const transformChaptersResponse = backendChapters => {
  const chapters = {};
  const steps = {};
  const milestoneIdMapping = {}; // Store mapping: { chapterId: { stepId: milestoneId } }

  backendChapters.forEach(chapter => {
    // IMPORTANT: Backend uses chapterNumber as the ID (1, 2, 3...)
    // Frontend DEFAULT_CONTENT also uses chapterNumber as the key
    const chapterId = chapter.chapterNumber || chapter.id;

    // Get default content for this chapter using chapterNumber as key
    // This ensures we match frontend hardcoded data with backend data correctly
    const defaultChapter = DEFAULT_CONTENT.chapters[chapterId] || {};

    // Transform chapter - merge backend data with frontend defaults
    // Backend provides: id, chapterNumber, chapterName, milestones
    // Frontend DEFAULT_CONTENT provides: description, backgroundImage, heroLogo, completionMessage, subtitle
    chapters[chapterId] = {
      id: chapterId, // chapterNumber is the display ID (1, 2, 3...)
      chapterName: chapter.chapterName || defaultChapter.chapterName || '', // Backend CMS takes priority
      order: chapter.chapterNumber || chapterId, // chapterNumber is the order
      // Use frontend defaults for display fields (not stored in backend)
      description: defaultChapter.description || '',
      backgroundImage: defaultChapter.backgroundImage || '',
      heroLogo: defaultChapter.heroLogo || '',
      completionMessage: defaultChapter.completionMessage || '',
      subtitle: defaultChapter.subtitle || '', // Also from frontend defaults
      // Store original chapterNumber for reference
      chapterNumber: chapter.chapterNumber,
    };

    // Transform milestones to steps
    // IMPORTANT: Steps come ONLY from backend milestones, NOT from DEFAULT_CONTENT
    // Each milestone from backend becomes a step in the frontend
    if (chapter.milestones && Array.isArray(chapter.milestones)) {
      steps[chapterId] = {};
      milestoneIdMapping[chapterId] = {};

      chapter.milestones.forEach((milestone, index) => {
        // Create stepId in format 'step-1', 'step-2', etc. based on milestone chapterOrder
        const stepId = `step-${milestone.chapterOrder || index + 1}`;
        const milestoneId = milestone.id;

        // Store mapping for sync purposes: stepId → milestoneId
        milestoneIdMapping[chapterId][stepId] = milestoneId;

        // Extract videoUrl from metadata if present
        const metadata = milestone.metadata || {};
        const videoUrl = metadata.videoUrl || '';

        // Transform milestone to step - all data comes from backend milestone
        steps[chapterId][stepId] = {
          id: stepId,
          type: milestone.templateId || milestone.type || 'video', // Backend: templateId
          order: milestone.chapterOrder || index + 1, // Backend: chapterOrder
          metadata: {
            videoUrl: videoUrl, // From milestone.metadata.videoUrl
            ...metadata, // Include all metadata from backend
          },
          // Store milestoneId for sync purposes
          milestoneId: milestoneId, // Backend milestone database ID
        };
      });
    }
  });

  return { chapters, steps, milestoneIdMapping };
};

// Transform backend progress response to frontend format
// Backend: { onboarding: { flag }, completions: [{ id, chapterMilestoneId, videoProgress, isCompleted }] }
// Frontend: { completedSteps: {...}, chapterProgress: {...}, completedMilestones: {...}, videoProgress: {...} }
const transformProgressResponse = (backendResponse, milestoneIdMapping, chapters, steps) => {
  const completedSteps = {};
  const chapterProgress = {};
  const completedMilestones = {};
  const videoProgress = {};

  if (!backendResponse || !backendResponse.completions) {
    return { completedSteps, chapterProgress, completedMilestones };
  }

  // Group completions by chapter and track completed milestone IDs
  const completionsByChapter = {};

  backendResponse.completions.forEach(completion => {
    // Store completed milestone ID directly
    // Normalize to number to ensure consistent type matching
    if (completion.isCompleted) {
      const milestoneId =
        typeof completion.chapterMilestoneId === 'number'
          ? completion.chapterMilestoneId
          : parseInt(completion.chapterMilestoneId, 10);
      completedMilestones[milestoneId] = true;
    }

    // Find which chapter and step this milestone belongs to
    let foundChapterId = null;
    let foundStepId = null;

    for (const [chapterId, stepMapping] of Object.entries(milestoneIdMapping)) {
      for (const [stepId, milestoneId] of Object.entries(stepMapping)) {
        if (milestoneId === completion.chapterMilestoneId) {
          foundChapterId = chapterId;
          foundStepId = stepId;
          break;
        }
      }
      if (foundChapterId) break;
    }

    if (foundChapterId && foundStepId) {
      if (!completionsByChapter[foundChapterId]) {
        completionsByChapter[foundChapterId] = [];
      }
      completionsByChapter[foundChapterId].push({
        stepId: foundStepId,
        isCompleted: completion.isCompleted,
        videoProgress: completion.videoProgress,
      });

      // Store videoProgress if it exists
      if (completion.videoProgress !== null && completion.videoProgress !== undefined) {
        const chapterIdStr = String(foundChapterId);
        if (!videoProgress[chapterIdStr]) {
          videoProgress[chapterIdStr] = {};
        }
        // Convert videoProgress string to number for storage
        const progressSeconds = parseFloat(completion.videoProgress);
        if (!isNaN(progressSeconds)) {
          videoProgress[chapterIdStr][foundStepId] = progressSeconds;
        }
      }
    }
  });

  // Get all chapter IDs sorted
  const chapterIds = Object.keys(chapters || {})
    .map(id => parseInt(id))
    .sort((a, b) => a - b);

  // Build frontend progress structure
  chapterIds.forEach(chapterId => {
    const chapterIdStr = String(chapterId);
    const chapterCompletions = completionsByChapter[chapterIdStr] || [];

    // Initialize chapter progress
    if (!completedSteps[chapterIdStr]) {
      completedSteps[chapterIdStr] = {};
    }

    // Track completed steps and find last completed step
    let lastCompletedStepId = null;
    chapterCompletions.forEach(completion => {
      if (completion.isCompleted) {
        completedSteps[chapterIdStr][completion.stepId] = true;
        lastCompletedStepId = completion.stepId;
      }
    });

    // Set chapter progress to last completed step
    if (lastCompletedStepId) {
      chapterProgress[chapterIdStr] = lastCompletedStepId;
    }
  });

  return { completedSteps, chapterProgress, completedMilestones, videoProgress };
};

// Default sample content (used only when no cached content is available)
const DEFAULT_CONTENT = {
  chapters: {
    1: {
      id: 1,
      order: 1,
      chapterName: "God's Design for Family",
      subtitle: 'A father, a mother <br> and their little ones',
      description: '',
      backgroundImage: '/assets/absolutely/chapter1/background.webp',
      heroLogo: REMOTE_ASSETS.absolutelyChapter1Title,
      completionMessage:
        "<p>Before we find the special book...</p><h1>Let's pray together <br> for our family</h1>",
    },
    2: {
      id: 2,
      order: 1,
      chapterName: 'Find the great adventurer',
      subtitle: "God's Word guides <br> us through life",
      description: '',
      backgroundImage: '/assets/absolutely/chapter2/background.webp',
      heroLogo: '/assets/absolutely/chapter2/title.webp',
      completionMessage:
        '<p>Parents, you start first.</p><h1>Share how you chose the <br> narrow path even when <br> it was difficult</h1>',
    },
    3: {
      id: 3,
      order: 1,
      chapterName: 'The Growing Up Podcast',
      subtitle: 'God created you <br> just right!',
      description: '',
      backgroundImage: '/assets/absolutely/chapter3/background.webp',
      heroLogo: '/assets/absolutely/chapter3/title.webp',
      completionMessage:
        "<p>Kids, now it's your turn.</p><h1>share your strengths <br> and your dreams</h1>",
    },
  },
  steps: {
    1: {
      'step-1': {
        id: 'step-1',
        type: 'video',
        metadata: {
          videoUrl: 'https://cdn.plyr.io/static/demo/View_From_A_Blue_Moon_Trailer-576p.mp4',
        },
      },
    },
    2: {
      'step-1': {
        id: 'step-1',
        type: 'video',
        metadata: {
          videoUrl: 'https://cdn.plyr.io/static/demo/View_From_A_Blue_Moon_Trailer-576p.mp4',
        },
      },
    },
    3: {
      'step-1': {
        id: 'step-1',
        type: 'video',
        metadata: {
          videoUrl: 'https://cdn.plyr.io/static/demo/View_From_A_Blue_Moon_Trailer-576p.mp4',
        },
      },
    },
  },
};

// Load initial data from storage
// TEMPORARILY DISABLED - using API only for testing
const storedProgress = loadProgressFromStorage();
const storedContent = loadContentFromStorage();

// Initialize default progress state
// TEMPORARILY DISABLED localStorage - using API only for testing
const getInitialProgress = () => {
  // Always return empty progress - rely on API only
  return {
    memberOnboardingFlags: {},
    memberCompletedSteps: {},
    memberChapterProgress: {},
    memberCompletedMilestones: {},
    memberVideoProgress: {},
  };
};

const state = () => ({
  ...createInitialState(),
  // TEMPORARILY DISABLED localStorage - using API only for testing
  ...getInitialProgress(),
  // TEMPORARILY DISABLED localStorage - using API only for testing
  // IMPORTANT: Steps come ONLY from backend milestones, not from DEFAULT_CONTENT
  // DEFAULT_CONTENT.steps is only used as fallback if backend fails AND no cached data exists
  chapters: DEFAULT_CONTENT.chapters, // Always use DEFAULT_CONTENT initially, will be replaced by API
  steps: {}, // Steps come from backend milestones only - will be populated by API
  contentLastUpdated: null,
});

const mutations = {
  // ============================================
  // CONTENT MUTATIONS (Static data)
  // ============================================
  SET_LOADING_CONTENT(state, isLoading) {
    state.isLoadingContent = isLoading;
  },
  SET_CHAPTERS(state, chapters) {
    state.chapters = chapters;
  },
  SET_STEPS(state, steps) {
    state.steps = steps;
  },
  SET_CONTENT_LAST_UPDATED(state, timestamp) {
    state.contentLastUpdated = timestamp;
  },

  // ============================================
  // PROGRESS MUTATIONS (Member-specific data)
  // ============================================
  SET_LOADING_PROGRESS(state, isLoading) {
    state.isLoadingProgress = isLoading;
  },
  SET_MEMBER_ONBOARDING_FLAGS(state, flags) {
    state.memberOnboardingFlags = { ...state.memberOnboardingFlags, ...flags };
  },
  SET_MEMBER_COMPLETED_STEP(state, { chapterId, stepId, completed }) {
    if (!state.memberCompletedSteps[chapterId]) {
      state.memberCompletedSteps = {
        ...state.memberCompletedSteps,
        [chapterId]: {},
      };
    }
    state.memberCompletedSteps = {
      ...state.memberCompletedSteps,
      [chapterId]: {
        ...state.memberCompletedSteps[chapterId],
        [stepId]: completed,
      },
    };
  },
  SET_MEMBER_CHAPTER_PROGRESS(state, { chapterId, lastStepId }) {
    state.memberChapterProgress = {
      ...state.memberChapterProgress,
      [chapterId]: lastStepId,
    };
  },
  SET_MEMBER_COMPLETED_MILESTONE(state, { milestoneId, completed }) {
    if (completed) {
      state.memberCompletedMilestones = {
        ...state.memberCompletedMilestones,
        [milestoneId]: true,
      };
    } else {
      // Remove milestone from completed list if uncompleted
      const { [milestoneId]: removed, ...rest } = state.memberCompletedMilestones;
      state.memberCompletedMilestones = rest;
    }
  },
  SET_MEMBER_VIDEO_PROGRESS(state, { chapterId, stepId, videoProgress }) {
    const chapterIdStr = String(chapterId);
    if (!state.memberVideoProgress[chapterIdStr]) {
      state.memberVideoProgress = {
        ...state.memberVideoProgress,
        [chapterIdStr]: {},
      };
    }
    state.memberVideoProgress = {
      ...state.memberVideoProgress,
      [chapterIdStr]: {
        ...state.memberVideoProgress[chapterIdStr],
        [stepId]: videoProgress,
      },
    };
  },
  RESET_PROGRESS(state) {
    // Reset only member progress, keep content
    state.memberOnboardingFlags = {};
    state.memberCompletedSteps = {};
    state.memberChapterProgress = {};
    state.memberCompletedMilestones = {};
    state.memberVideoProgress = {};
    localStorage.removeItem('absolutely_member_onboardingFlags');
    localStorage.removeItem('absolutely_member_completedSteps');
    localStorage.removeItem('absolutely_member_chapterProgress');
  },
  RESET_CONTENT(state) {
    // Reset only content cache
    state.chapters = {};
    state.steps = {};
    state.contentLastUpdated = null;
    localStorage.removeItem('absolutely_content_chapters');
    localStorage.removeItem('absolutely_content_steps');
    localStorage.removeItem('absolutely_content_lastUpdated');
  },
  RESET_ALL(state) {
    // Reset everything
    Object.assign(state, createInitialState());
    localStorage.removeItem('absolutely_member_onboardingFlags');
    localStorage.removeItem('absolutely_member_completedSteps');
    localStorage.removeItem('absolutely_member_chapterProgress');
    localStorage.removeItem('absolutely_content_chapters');
    localStorage.removeItem('absolutely_content_steps');
    localStorage.removeItem('absolutely_content_lastUpdated');
  },

  // ============================================
  // UI MUTATIONS (Component state)
  // ============================================
  SET_SELECTED_CHAPTER_ID(state, chapterId) {
    state.selectedChapterId = chapterId;
  },

  // ============================================
  // UNLOCK MUTATIONS
  // ============================================
  SET_UNLOCKING(state, isUnlocking) {
    state.isUnlocking = isUnlocking;
  },
  SET_UNLOCKING_ERROR(state, error) {
    state.unlockingError = error;
  },

  // ============================================
  // VIDEO MUTATIONS
  // ============================================
  SET_VIDEO_PROGRESS_AT_END(state, progress) {
    state.videoProgressAtEnd = progress;
  },
};

const getters = {
  // ============================================
  // CONTENT GETTERS (Static data)
  // ============================================
  getChapter: state => chapterId => {
    return state.chapters[chapterId] || null;
  },
  getChapters: state => {
    return Object.values(state.chapters).sort((a, b) => a.order - b.order);
  },
  getTitleById: state => chapterId => {
    return state.chapters[chapterId]?.chapterName || '';
  },
  getStep: state => (chapterId, stepId) => {
    return state.steps[chapterId]?.[stepId] || null;
  },
  getSteps: state => chapterId => {
    const chapterSteps = state.steps[chapterId];
    if (!chapterSteps) return [];
    return Object.values(chapterSteps).sort((a, b) => {
      // Handle steps without order field
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });
  },
  getTotalSteps: state => chapterId => {
    // Count actual steps from store instead of using totalSteps field
    const chapterSteps = state.steps[chapterId];
    if (!chapterSteps) return 0;
    return Object.keys(chapterSteps).length;
  },
  hasContent: state => {
    return Object.keys(state.chapters).length > 0;
  },

  // ============================================
  // PROGRESS GETTERS (Member-specific data)
  // ============================================
  getOnboardingFlags: state => {
    return state.memberOnboardingFlags || {};
  },
  hasVisitedAbsolutely: state => {
    return state.memberOnboardingFlags?.hasVisitedAbsolutely === true;
  },
  isChapterUnlocked: (state, getters) => chapterId => {
    // All chapters are unlocked - check if chapter exists
    return getters.getChapter(chapterId) !== null;
  },
  isStepCompleted: state => (chapterId, stepId) => {
    return state.memberCompletedSteps[chapterId]?.[stepId] === true;
  },
  getChapterProgress: state => chapterId => {
    return state.memberChapterProgress[chapterId] || null;
  },
  getCompletedStepsCount: state => chapterId => {
    const steps = state.memberCompletedSteps[chapterId];
    if (!steps) return 0;
    return Object.values(steps).filter(completed => completed === true).length;
  },
  getProgressPercentage: (state, getters) => chapterId => {
    const totalSteps = getters.getTotalSteps(chapterId);
    if (totalSteps === 0) return 0;
    const completedSteps = getters.getCompletedStepsCount(chapterId);
    return Math.round((completedSteps / totalSteps) * 100);
  },
  getVideoProgress: state => (chapterId, stepId) => {
    const chapterIdStr = String(chapterId);
    return state.memberVideoProgress[chapterIdStr]?.[stepId] || null;
  },
  // Check if a chapter is fully completed by checking if all milestones are completed
  isChapterCompleted: (state, getters) => chapterId => {
    // Get all milestones for this chapter from content
    const chapterSteps = getters.getSteps(chapterId);
    if (!chapterSteps || chapterSteps.length === 0) {
      return false; // No milestones means chapter is not completed
    }

    // Check if ALL milestones for this chapter are completed
    const allMilestonesCompleted = chapterSteps.every(step => {
      // Each step has a milestoneId property (from transformChaptersResponse)
      const milestoneId = step.milestoneId;
      if (!milestoneId) {
        return false; // If step doesn't have milestoneId, consider it not completed
      }
      // Normalize milestoneId to number for consistent comparison
      const normalizedMilestoneId =
        typeof milestoneId === 'number' ? milestoneId : parseInt(milestoneId, 10);

      const isCompleted =
        state.memberCompletedMilestones[normalizedMilestoneId] === true ||
        state.memberCompletedMilestones[String(normalizedMilestoneId)] === true;

      return isCompleted;
    });

    return allMilestonesCompleted;
  },
  // Get the current chapter ID based on progress
  // Returns:
  // - First chapter that is NOT completed (has progress but not completed, or hasn't started)
  // - First completed chapter where next chapter hasn't started (move to next)
  // - Highest chapter if all are completed
  getCurrentChapterId: (state, getters) => {
    const chapters = getters.getChapters;
    if (chapters.length === 0) {
      return 1;
    }

    // Sort chapters by order
    const sortedChapters = [...chapters].sort((a, b) => a.order - b.order);

    // Find the first chapter that is NOT completed
    // isChapterCompleted already checks isCompleted flag for all milestones (including video)
    // So if a video has progress but isCompleted is false, isChapterCompleted will return false
    for (const chapter of sortedChapters) {
      const isCompleted = getters.isChapterCompleted(chapter.id);

      if (!isCompleted) {
        return chapter.id;
      }
    }

    // If all chapters are completed, return the highest chapter
    const highestChapter = sortedChapters[sortedChapters.length - 1];
    const result = highestChapter ? highestChapter.id : 1;
    return result;
  },
  // Get the currently selected chapter ID (for UI state)
  getSelectedChapterId: state => {
    return state.selectedChapterId || 0;
  },
};

const actions = {
  // ============================================
  // CONTENT ACTIONS (Static data from CMS/API)
  // ============================================

  // Fetch chapters and steps from API
  async fetchContent({ commit, state, rootState }, options = {}) {
    try {
      // Don't refetch if content exists and not forcing refresh
      // Check steps instead of chapters because chapters has DEFAULT_CONTENT
      // but steps is only populated by API - this ensures API is called on first load
      if (Object.keys(state.steps).length > 0 && !options?.refresh) {
        return { chapters: state.chapters, steps: state.steps };
      }

      commit('SET_LOADING_CONTENT', true);
      const accessToken = rootState.user.token;

      // Use correct endpoint: /absolutely/chapters
      const response = await axios.get(process.env.VUE_APP_API_URL + '/absolutely/chapters', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.data && Array.isArray(response.data)) {
        // Transform backend format to frontend format
        const { chapters, steps, milestoneIdMapping } = transformChaptersResponse(response.data);

        commit('SET_CHAPTERS', chapters);
        commit('SET_STEPS', steps);
        commit('SET_CONTENT_LAST_UPDATED', Date.now());

        // Cache content to localStorage
        saveContentToStorage(chapters, steps);

        // Store milestoneIdMapping in state for progress syncing
        // We'll store it in a separate mutation or use it directly in syncProgress
        state._milestoneIdMapping = milestoneIdMapping;
      }

      return { chapters: state.chapters, steps: state.steps };
    } catch (error) {
      console.error('Error fetching absolutely content:', error);
      // Return cached content if API fails
      return { chapters: state.chapters, steps: state.steps };
    } finally {
      commit('SET_LOADING_CONTENT', false);
    }
  },

  // Clear content cache (force refresh on next fetch)
  clearContentCache({ commit }) {
    commit('RESET_CONTENT');
  },

  // ============================================
  // PROGRESS ACTIONS (Member-specific data)
  // ============================================

  // Complete a step
  async completeStep({ commit, dispatch, state, getters }, { chapterId, stepId, videoProgress }) {
    // Get the step to find its milestoneId
    const step = getters.getStep(chapterId, stepId);
    const milestoneId = step?.milestoneId;

    // Update local state immediately
    commit('SET_MEMBER_COMPLETED_STEP', { chapterId, stepId, completed: true });
    commit('SET_MEMBER_CHAPTER_PROGRESS', { chapterId, lastStepId: stepId });

    // Also store milestone completion if milestoneId exists
    if (milestoneId) {
      commit('SET_MEMBER_COMPLETED_MILESTONE', { milestoneId, completed: true });
    }

    saveProgressToStorage(
      state.memberOnboardingFlags,
      state.memberCompletedSteps,
      state.memberChapterProgress,
    );

    // Sync with backend - mark as completed (backend tracks latest progress)
    await dispatch('syncProgress', {
      chapterId,
      stepId,
      isCompleted: true,
      videoProgress: videoProgress, // Send final progress to backend
    });
  },

  // Mark step as incomplete (for reset/testing)
  uncompleteStep({ commit, state }, { chapterId, stepId }) {
    commit('SET_MEMBER_COMPLETED_STEP', { chapterId, stepId, completed: false });
    saveProgressToStorage(
      state.memberOnboardingFlags,
      state.memberCompletedSteps,
      state.memberChapterProgress,
    );
  },

  // Sync progress with server (per milestone)
  async syncProgress(
    { commit, rootState, state },
    { chapterId, stepId, isCompleted, videoProgress },
  ) {
    try {
      commit('SET_LOADING_PROGRESS', true);
      const accessToken = rootState.user.token;

      // Get milestoneIdMapping from state
      const milestoneIdMapping = state._milestoneIdMapping || {};
      const milestoneId = milestoneIdMapping[chapterId]?.[stepId];

      if (!milestoneId) {
        console.warn(`No milestoneId found for chapter ${chapterId}, step ${stepId}`);
        return null;
      }

      // Use correct endpoint: /absolutely/chapter (per milestone)
      const response = await axios.post(
        process.env.VUE_APP_API_URL + '/absolutely/chapter',
        {
          chapterMilestoneId: milestoneId,
          isCompleted: isCompleted !== undefined ? isCompleted : true,
          videoProgress: videoProgress,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      // Update local state if sync successful
      if (response.data) {
        // Update completion status if provided
        // Backend always returns isCompleted (boolean, default false)
        if (response.data.isCompleted) {
          // Backend keeps latest progress, localStorage tracks current session
          // Don't reset backend progress - it represents the latest watched position
          commit('SET_MEMBER_COMPLETED_STEP', {
            chapterId,
            stepId,
            completed: response.data.isCompleted,
          });
          commit('SET_MEMBER_CHAPTER_PROGRESS', {
            chapterId,
            lastStepId: stepId,
          });
          // Also store milestone completion
          if (milestoneId) {
            commit('SET_MEMBER_COMPLETED_MILESTONE', {
              milestoneId,
              completed: response.data.isCompleted,
            });
          }
        } else if (videoProgress !== undefined && videoProgress !== null) {
          // Update video progress if not completed
          const progressSeconds =
            typeof videoProgress === 'string' ? parseFloat(videoProgress) : videoProgress;
          if (!isNaN(progressSeconds)) {
            commit('SET_MEMBER_VIDEO_PROGRESS', {
              chapterId,
              stepId,
              videoProgress: progressSeconds,
            });
          }
        }
        saveProgressToStorage(
          state.memberOnboardingFlags,
          state.memberCompletedSteps,
          state.memberChapterProgress,
        );
      }

      return response.data;
    } catch (error) {
      console.error('Error syncing absolutely progress:', error);
      // Don't throw - allow offline usage
      return null;
    } finally {
      commit('SET_LOADING_PROGRESS', false);
    }
  },

  // Load progress from server
  async loadProgress({ commit, rootState, state }) {
    try {
      commit('SET_LOADING_PROGRESS', true);
      const accessToken = rootState.user.token;

      // Use correct endpoint: /absolutely/me
      console.log('[loadProgress] Calling /absolutely/me API...');
      const response = await axios.get(process.env.VUE_APP_API_URL + '/absolutely/me', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      console.log('[loadProgress] API response:', response.data);
      console.log('[loadProgress] onboarding flags in response:', response.data?.onboarding);

      if (response.data) {
        // Get milestoneIdMapping from state (set during fetchContent)
        const milestoneIdMapping = state._milestoneIdMapping || {};

        // Load onboarding flags from backend response
        if (response.data.onboarding) {
          console.log('[loadProgress] Committing onboarding flags:', response.data.onboarding);
          commit('SET_MEMBER_ONBOARDING_FLAGS', response.data.onboarding);
          console.log('[loadProgress] State after commit:', state.memberOnboardingFlags);
        } else {
          console.warn('[loadProgress] No onboarding flags in response.data.onboarding');
        }

        // Transform backend format to frontend format
        const { completedSteps, chapterProgress, completedMilestones, videoProgress } =
          transformProgressResponse(response.data, milestoneIdMapping, state.chapters, state.steps);

        // Update state with transformed progress
        Object.keys(completedSteps).forEach(chapterId => {
          Object.keys(completedSteps[chapterId]).forEach(stepId => {
            commit('SET_MEMBER_COMPLETED_STEP', {
              chapterId,
              stepId,
              completed: completedSteps[chapterId][stepId],
            });
          });
        });

        Object.keys(chapterProgress).forEach(chapterId => {
          commit('SET_MEMBER_CHAPTER_PROGRESS', {
            chapterId,
            lastStepId: chapterProgress[chapterId],
          });
        });

        // Update completed milestones
        // Ensure milestoneId is normalized to number for consistent storage
        Object.keys(completedMilestones).forEach(milestoneIdKey => {
          const milestoneId =
            typeof milestoneIdKey === 'number' ? milestoneIdKey : parseInt(milestoneIdKey, 10);
          commit('SET_MEMBER_COMPLETED_MILESTONE', {
            milestoneId: milestoneId,
            completed: completedMilestones[milestoneIdKey],
          });
        });

        // Update video progress
        Object.keys(videoProgress).forEach(chapterId => {
          Object.keys(videoProgress[chapterId]).forEach(stepId => {
            commit('SET_MEMBER_VIDEO_PROGRESS', {
              chapterId: parseInt(chapterId, 10),
              stepId,
              videoProgress: videoProgress[chapterId][stepId],
            });
          });
        });

        saveProgressToStorage(state.memberOnboardingFlags, completedSteps, chapterProgress);
      }

      return response.data;
    } catch (error) {
      console.error('Error loading absolutely progress:', error);
      // Don't throw - use local storage data
      return null;
    } finally {
      commit('SET_LOADING_PROGRESS', false);
    }
  },

  // Reset user progress (for testing/admin)
  resetProgress({ commit }) {
    commit('RESET_PROGRESS');
  },

  // Reset content cache (for testing/admin)
  resetContent({ commit }) {
    commit('RESET_CONTENT');
  },

  // Set selected chapter ID (for UI state)
  setSelectedChapterId({ commit }, chapterId) {
    commit('SET_SELECTED_CHAPTER_ID', chapterId);
  },

  // Reset everything (for testing/admin)
  resetAll({ commit }) {
    commit('RESET_ALL');
  },

  // ============================================
  // UNLOCK ACTIONS
  // ============================================

  // Initiate unlock process with retry logic
  async initiateUnlock({ commit, rootState }) {
    try {
      commit('SET_UNLOCKING', true);
      commit('SET_UNLOCKING_ERROR', null);

      // Retry API call with exponential backoff (2 retries, 1s initial delay)
      await retryWithBackoff(
        async () => {
          const accessToken = rootState.user.token;

          return await axios.post(
            `${process.env.VUE_APP_API_URL}/absolutely/access`,
            {},
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
          );
        },
        2,
        1000,
      );

      console.log('[Absolutely] Unlock API call successful');
      return true;
    } catch (error) {
      console.error('[Absolutely] Error during unlock after retries:', error);
      commit('SET_UNLOCKING_ERROR', error.message);
      commit('SET_UNLOCKING', false);
      throw error;
    } finally {
      commit('SET_UNLOCKING', false);
    }
  },

  // ============================================
  // ONBOARDING ACTIONS
  // ============================================

  // Sync onboarding flags with server
  async syncOnboarding({ commit, rootState, state }, flags) {
    try {
      const accessToken = rootState.user.token;

      // Use endpoint: /absolutely/onboarding
      const response = await axios.post(
        process.env.VUE_APP_API_URL + '/absolutely/onboarding',
        { flags },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      // Update local state with merged flags from server
      if (response.data) {
        commit('SET_MEMBER_ONBOARDING_FLAGS', response.data);
        saveProgressToStorage(
          state.memberOnboardingFlags,
          state.memberCompletedSteps,
          state.memberChapterProgress,
        );
      }

      return response.data;
    } catch (error) {
      console.error('Error syncing absolutely onboarding flags:', error);
      // Don't throw - allow offline usage, update local state anyway
      commit('SET_MEMBER_ONBOARDING_FLAGS', flags);
      saveProgressToStorage(
        state.memberOnboardingFlags,
        state.memberCompletedSteps,
        state.memberChapterProgress,
      );
      return null;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
